import React from 'react';
import {
  BREAKPOINTS,
  centerContent,
  getHexToRgb,
  getRem,
  transitionAnimation,
  typographyBody2,
  typographyCaption,
  truncateText,
  wordBreak,
} from 'lib/core';
import PropTypes from 'prop-types';
import styled, { createGlobalStyle, css } from 'styled-components';

import { TABLE_LAYOUT_OPTIONS } from '../constants';

const agDndGhostClass = '.ag-dnd-ghost';
const tableClass = '.cdk-rds-table';
const agThemeRadial = 'ag-theme-radial';

const checkboxUncheckedIcon =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSIjNWI1ZjY2IiBkPSJNMTkuMzk4IDV2MTRoLTE0VjVoMTR6bTAtMmgtMTRjLTEuMSAwLTIgLjktMiAydjE0YzAgMS4xLjkgMiAyIDJoMTRjMS4xIDAgMi0uOSAyLTJWNWMwLTEuMS0uOS0yLTItMnoiLz48L3N2Zz4=';
const checkboxCheckedIcon =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSIjMDA2Y2ZhIiBkPSJNMTkuMjM2IDNoLTE0Yy0xLjEgMC0yIC45LTIgMnYxNGMwIDEuMS45IDIgMiAyaDE0YzEuMSAwIDItLjkgMi0yVjVjMC0xLjEtLjktMi0yLTJ6Ii8+PHBhdGggZmlsbD0iI2ZmZiIgZD0iTTEwLjIzNiAxN2wtNS00LjgwOCAxLjQtMS4zNDYgMy42IDMuNDYyTDE3LjgzNiA3bDEuNCAxLjM0NnoiLz48L3N2Zz4=';
const checkboxIndeterminateIcon =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSIjMDA2Y2ZhIiBkPSJNMTkuMDc1IDNhMiAyIDAgMDEyIDJ2MTRhMiAyIDAgMDEtMiAyaC0xNGEyIDIgMCAwMS0yLTJWNWEyIDIgMCAwMTItMmgxNHptLTEgOGgtMTJ2MmgxMnYtMnoiIGZpbGwtcnVsZT0iZXZlbm9kZCIvPjwvc3ZnPg==';

const checkboxIndicatorBackgroundStyles = (state) => css`
  .ag-checkbox-input-wrapper {
    &::after {
      ${state === 'hover' &&
      css`
        background-color: ${({ theme }) => theme.color.state.default.hover.value};
      `}

      ${state === 'pressed' &&
      css`
        background-color: ${({ theme }) => theme.color.state.default.pressed.value};
      `}
    }

    &.ag-checked,
    &.ag-indeterminate {
      &::after {
        ${state === 'hover' &&
        css`
          background-color: ${({ theme }) => theme.color.state.primary.hover.value};
        `}

        ${state === 'pressed' &&
        css`
          background-color: ${({ theme }) => theme.color.state.primary.pressed.value};
        `}
      }
    }
  }
`;

export const TableColumnDndContainer = createGlobalStyle`
    .ag-theme-balham${agDndGhostClass} {
        border: 0;
        border-radius: ${getRem(3)};
        box-shadow: 0 0 0 3px red;
        flex-direction: row-reverse;
        padding: 0 0 0 ${getRem(11)};

        ${/* sc-selector */ agDndGhostClass}-icon {
            height: ${getRem(32)};
            margin-right: ${getRem(5)};
            opacity: 1;
        }

        ${/* sc-selector */ agDndGhostClass}-label {
            color: ${({ theme }) => theme.color.text.light.disabled.value};
            font-family: ${({ theme }) => theme.font.family.main.value};
            font-size: ${getRem(12)};
            font-weight: 400;
            padding-right: ${getRem(40)};
        }

        .ag-icon-arrows,
        .ag-icon-eye-slash,
        .ag-icon-left,
        .ag-icon-right {
            display: none;
        }
    }
`;

const StyledTableContainer = styled.div.attrs({ className: `ag-theme-balham cdk-rds-table ${agThemeRadial}` })`
  && {
    width: 100%;
    ${({
      $agGridVersion = undefined,
      $hideBorder = true,
      $hideTableHeader = false,
      $isMobile = false,
      $showCustomHeaderContent = false,
      $tableLayout = TABLE_LAYOUT_OPTIONS.NORMAL,
      theme,
    }) => {
      let cssToUse = '';
      const agGridNumericVersion = parseInt($agGridVersion);
      if (agGridNumericVersion >= 28) {
        cssToUse = css`
          /* AG Grid v28+ CSS Styling Variables */
          --ag-background-color: ${theme.color.additional.light.value};
          --ag-balham-active-color: ${theme.color.primary.value};
          --ag-borders: ${$hideBorder ? 'none' : 'solid 1px'};
          --ag-font-family: ${theme.font.family.main.value};
          --ag-font-size: ${getRem(14)};
          --ag-header-background-color: ${theme.color.additional.light.value};
          --ag-header-foreground-color: ${theme.color.text.light.primary.value};
          --ag-header-height: 32px;
          --ag-odd-row-background-color: ${theme.color.zebraStripe.background.value};
          --ag-row-hover-color: ${theme.color.zebraStripe.hover.value};

          /* Addition CSS necessary for the Radial style */

          .ag-cell {
            display: flex;
            align-items: center;
          }
          .ag-header {
            border-bottom: 2px solid ${theme.color.text.light.primary.value};
            font-size: ${getRem(12)};
            font-weight: 500;
          }

          .ag-numeric-cell--align-right {
            text-align: right;
            justify-content: end;
          }

          .ag-cell--checkbox {
            &:hover {
              ${checkboxIndicatorBackgroundStyles('hover')};
            }

            &:active {
              ${checkboxIndicatorBackgroundStyles('pressed')};
            }
          }

          .ag-checkbox-input-wrapper {
            background-color: transparent;
            background-image: url('${checkboxUncheckedIcon}');
            content: '';
            cursor: pointer;
            height: ${getRem(24)};
            width: ${getRem(24)};

            &:focus-within {
              box-shadow: none;
            }

            &::after {
              ${centerContent}
              ${transitionAnimation('background-color')}

                border-radius: 50%;
              content: '';
              display: inline-block;
              height: ${getRem(32)};
              width: ${getRem(32)};
            }
          }

          .ag-checkbox-input-wrapper.ag-checked {
            background-image: url('${checkboxCheckedIcon}');

            &::after {
              ${centerContent}
              content: '';
            }
          }

          .ag-checkbox-input-wrapper.ag-indeterminate {
            background-image: url('${checkboxIndeterminateIcon}');

            &::after {
              ${centerContent}
              content: '';
            }
          }

          .ag-checkbox-input {
            cursor: pointer;
          }

          .ag-row-hover {
            background-color: ${theme.color.zebraStripe.hover.value};
            &::before {
              background-color: ${theme.color.gray[600].value};
              content: '';
              height: 100%;
              left: 0;
              position: absolute;
              top: 0;
              width: ${theme.size.spacing.small.value};
            }
          }
        `;
      } else {
        cssToUse = css`
          /* Overall table styles */
          color: ${theme.color.text.light.primary.value};
          font-family: ${theme.font.family.main.value};
          font-size: ${getRem(14)};

          /* Table components layout */
          display: flex;
          flex-direction: column;
          ${$tableLayout === TABLE_LAYOUT_OPTIONS.NORMAL && 'height: 100%;'}

          .ag-layout-auto-height {
            min-height: ${getRem(100)};
          }

          .ag-body-viewport {
            background-color: ${theme.color.additional.light.value};
            overflow-y: visible;
          }

          .ag-react-container {
            display: flex;
            min-width: 0;
          }

          .ag-root-wrapper {
            border-bottom: none;
            border-color: ${theme.color.gray[200].value};

            ${$hideBorder && 'border: none;'}
            ${!$hideBorder && $hideTableHeader && $showCustomHeaderContent && 'border-top: none;'}
            ${!$hideBorder &&
            $hideTableHeader &&
            `border-top-left-radius: ${theme.size.borderRadius.large.value};
            border-top-right-radius: ${theme.size.borderRadius.large.value};`}
            ${!$hideBorder && !$hideTableHeader && 'border-top: none;'}
          }

          /* Overlay */

          .ag-overlay-panel {
            display: flex;
            justify-content: center;
          }

          .ag-overlay-no-rows-wrapper {
            width: 90%;
          }

          /* AG Grid column headers */
          .ag-header {
            background-color: ${theme.color.additional.light.value};
            border-bottom: 2px solid ${theme.color.text.light.primary.value};

            .ag-pinned-left-header {
              overflow: visible;
            }
          }

          .ag-header-row {
            color: ${theme.color.text.light.primary.value};
            font-size: ${getRem(12)};
            font-weight: 500;
          }

          .ag-pinned-left-header {
            border-right: 1px solid;
            border-right-color: ${theme.color.gray[200].value};
          }

          .ag-pinned-right-header {
            border-left: 1px solid;
            border-left-color: ${theme.color.gray[200].value};
          }

          /* AG Grid table rows */

          .ag-row {
            border-color: ${theme.color.gray[200].value};
            color: ${theme.color.text.light.primary.value};
            min-height: ${getRem(32)};
            z-index: ${theme.zIndex.tableRow.value};

            &:last-child {
              border-bottom-width: 0;
            }

            &.ag-row-focus {
              z-index: ${theme.zIndex.tableRowFocused.value};
            }
          }

          .ag-row-odd {
            background-color: ${theme.color.zebraStripe.background.value};
          }

          .ag-row-even {
            background-color: rgba(${getHexToRgb(theme.color.additional.light.value)});
          }

          .ag-row-hover {
            &::before {
              background-color: ${theme.color.gray[600].value};
              content: '';
              height: 100%;
              left: 0;
              position: absolute;
              top: 0;
              width: ${theme.size.spacing.small.value};
            }

            &:not(.ag-row-selected) {
              background-color: ${theme.color.zebraStripe.hover.value};
            }
          }

          .ag-row-selected {
            background-color: ${theme.color.primary[50].value};

            &:not(.ag-row-first) {
              border-color: ${theme.color.primary[200].value};

              & + .ag-row:not(.ag-row-first) {
                border-top-color: ${theme.color.primary[200].value};
              }
            }

            &:hover,
            &:focus {
              background-color: ${theme.color.primary[200].value};

              &::before {
                background-color: ${theme.color.primary[500].value};
              }
            }
          }

          /* AG Grid table cells */

          .ag-cell {
            align-items: center;
            display: inline-flex;
            font-family: ${theme.typography.table.fontFamily.value};
            font-weight: ${theme.font.weight.regular.value};
            overflow: visible;
            padding-left: ${theme.size.spacing.large.value};
            padding-right: ${theme.size.spacing.large.value};

            &__link {
              color: ${theme.color.primary[500].value};
              font-weight: 500;
              text-decoration: none;
            }

            &__text {
              &--highlighted {
                background-color: ${theme.color.primary[700].value};
                font-weight: 700;
                line-height: ${theme.size.spacing.large.value};
                color: ${theme.color.text.dark.primary.value};
                padding: 0.25rem 0.5rem;
              }

              &--overlay-text {
                border: 1px dashed ${theme.color.gray[400].value};
                padding: ${theme.size.spacing.large.value};
                white-space: pre-wrap;
              }

              &--truncate {
                ${truncateText}
              }

              &--truncate-wrap {
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }

            &--checkbox {
              &:hover {
                ${checkboxIndicatorBackgroundStyles('hover')};
              }

              &:active {
                ${checkboxIndicatorBackgroundStyles('pressed')};
              }
            }

            &--numeric {
              font-family: ${theme.font.family.alternate.value};
            }

            &--no-wrap {
              white-space: normal;
            }

            &.ag-cell-last-left-pinned:not(.ag-cell-range-right):not(.ag-cell-range-single-cell) {
              border-right: 1px solid;
              border-right-color: ${theme.color.gray[200].value};
            }

            &.ag-cell-first-right-pinned:not(.ag-cell-range-left):not(.ag-cell-range-single-cell) {
              border-left: 1px solid;
              border-left-color: ${theme.color.gray[200].value};
            }
          }

          /* Specific Cell Types */
          /* Selected  Cell */
          .ag-row-selected .ag-cell {
            &__link {
              color: ${theme.color.primary[600].value};
            }
          }

          /* Numeric Cell */
          .ag-numeric-cell {
            &--align-right {
              justify-content: flex-end;
            }
          }

          /* Header Cell */
          .ag-header-cell {
            font-weight: ${theme.typography.table.fontWeight.value};
            font-family: ${theme.typography.table.fontFamily.value};
            padding-left: ${theme.size.spacing.large.value};
            padding-right: ${theme.size.spacing.large.value};

            &.ag-header-cell-moving {
              background-color: ${theme.color.state.default.disabled.value};
              color: ${theme.color.text.light.disabled.value};
            }

            &:first-of-type:not(.ag-header-cell-moving),
            &:not(.ag-column-resizing) + .ag-header-cell:not(.ag-header-cell-moving) {
              &:hover {
                background-color: ${theme.color.state.default.hover.value};
              }
            }

            &::after {
              background-color: ${theme.color.gray[200].value};
              height: ${theme.size.spacing.large.value};
            }
          }

          /* Sortable Header Cell */

          .ag-header-cell-sortable {
            .ag-sort-none-icon {
              display: none;
            }

            &:hover {
              .ag-sort-none-icon {
                display: flex;
              }
            }
          }

          /* Header Cell with Keyboard Focus */

          .ag-keyboard-focus .ag-header-cell:focus::after {
            height: calc(100% - 1px);
            left: 0;
            top: 0;
            width: 100%;
          }

          /* A cell has focus, but does not have an internal range selected -- not sure what the purpose of this one is */
          .ag-ltr .ag-has-focus .ag-cell-focus:not(.ag-cell-range-selected) {
            border: 1px solid ${theme.color.primary[500].value};
          }

          /* AG Grid internal tooltips */
          .ag-tooltip {
            ${({ theme }) => typographyCaption(theme)};
            ${transitionAnimation('opacity')}
            ${wordBreak}

        background-color: ${theme.color.additional.dark.value};
            border: none;
            border-radius: ${theme.size.borderRadius.large.value};
            box-sizing: border-box;
            color: ${theme.color.additional.light.value};
            max-width: ${getRem(192)};
            padding: ${theme.size.spacing.medium.value} ${getRem(12)};
            user-select: none;
          }

          /* Adjustment for checkboxs when select all is used */
          ${tableClass}__checkbox {
            &--select-all {
              margin-left: ${getRem(1)};
            }
          }

          /* AG Grid specific cell type controls */

          .ag-menu,
          .ag-picker-field-wrapper {
            background-color: ${theme.color.additional.light.value};
            border-color: ${theme.color.gray[200].value};
          }

          .ag-input-field-input {
            background-color: ${theme.color.additional.light.value};
          }

          .ag-filter-body-wrapper {
            background-color: ${theme.color.additional.light.value};
            color: ${theme.color.text.light.primary.value};
            font-family: ${theme.font.family.main.value};
            font-size: ${getRem(14)};
          }

          .ag-icon-filter {
            color: rgba(${getHexToRgb(theme.color.additional.dark.value)}, 0.9);
          }

          .ag-icon-menu {
            color: rgba(${getHexToRgb(theme.color.additional.dark.value)}, 0.9);
          }

          .ag-icon-asc,
          .ag-icon-desc,
          .ag-icon-none {
            color: rgba(${getHexToRgb(theme.color.additional.dark.value)}, 0.54);
          }

          .ag-list-item {
            color: ${theme.color.additional.dark.value};
            background-color: ${theme.color.additional.light.value};
          }

          .ag-checkbox-input-wrapper {
            background-color: transparent;
            background-image: url('${checkboxUncheckedIcon}');
            content: '';
            cursor: pointer;
            height: ${getRem(24)};
            width: ${getRem(24)};

            &:focus-within {
              box-shadow: none;
            }

            &::after {
              ${centerContent}
              ${transitionAnimation('background-color')}

                border-radius: 50%;
              content: '';
              display: inline-block;
              height: ${getRem(32)};
              width: ${getRem(32)};
            }
          }

          .ag-checkbox-input-wrapper.ag-checked {
            background-image: url('${checkboxCheckedIcon}');

            &::after {
              ${centerContent}

              content: '';
            }
          }

          .ag-checkbox-input-wrapper.ag-indeterminate {
            background-image: url('${checkboxIndeterminateIcon}');

            &::after {
              ${centerContent}

              content: '';
            }
          }

          .ag-checkbox-input {
            cursor: pointer;
          }

          .ag-popup > div:not(.ag-tooltip-custom) {
            box-shadow: none;
          }

          /* Mobile styles */

          ${$isMobile &&
          `--ag-font-family: ${theme.typography.display3.fontFamily.value};
       --ag-font-size: ${theme.typography.display2.fontSize.value};
       --ag-font-weight: ${theme.typography.display2.fontWeight.value};
      ${typographyBody2(theme)};
      `}

          /* Media queries */

      @media ${BREAKPOINTS.L_MAX} {
            ${typographyBody2(theme)};
          }

          @media print {
            .ag-root {
              border: 1px solid;
              border-color: ${theme.color.gray[200].value};
              border-radius: ${theme.size.borderRadius.large.value};
            }

            .ag-cell {
              padding-left: ${getRem(10)};
              padding-right: ${getRem(10)};
            }

            .ag-header-cell {
              padding-left: ${getRem(10)};
              padding-right: ${getRem(10)};

              &::after {
                margin-bottom: ${theme.size.spacing.medium.value};
                margin-top: ${theme.size.spacing.medium.value};
              }
            }
          }
        `;
      }
      return cssToUse;
    }}
  }
`;

const TableContainer = React.forwardRef(
  (
    {
      agGridVersion,
      children,
      customTag,
      hideBorder,
      hideTableBorder,
      hideTitle,
      isMobile,
      showCustomHeaderContent,
      tableLayout,
      title,
      ...other
    },
    ref
  ) => {
    return (
      <StyledTableContainer
        $customTag={customTag}
        $hideTitle={hideTitle}
        $title={title}
        $agGridVersion={agGridVersion}
        $hideBorder={hideBorder}
        $hideTableBorder={hideTableBorder}
        $isMobile={isMobile}
        $showCustomHeaderContent={showCustomHeaderContent}
        $tableLayout={tableLayout}
        {...other}
        ref={ref}
      >
        {children}
      </StyledTableContainer>
    );
  }
);

TableContainer.propTypes = {
  /** The table */
  children: PropTypes.node,
  /** Ability to supply a different element instead of the default one.
   * Proper heading element (`h1`, `h2`, ...) must be provided according to page structure */
  customTag: PropTypes.elementType,
  /** If true, table title is visually hidden */
  hideTitle: PropTypes.bool,
  /** Sets a table title */
  title: PropTypes.node,
  /** If the string resolves to an integer >= 28 then CSS variables will be used for styling */
  agGridVersion: PropTypes.string,
  /** If true the table border won't be drawn */
  hideBorder: PropTypes.bool,
  /** If true the table header border won't be drawn */
  hideTableBorder: PropTypes.bool,
  /** If true the TableHeader won't be rendered */
  hideTableHeader: PropTypes.bool,
  /** If true the mobile stylings will be used */
  isMobile: PropTypes.bool,
  /** If true the custom header block will be rendered */
  showCustomHeaderContent: PropTypes.bool,
  /** Sets the style of table to render */
  tableLayout: PropTypes.oneOf(Object.values(TABLE_LAYOUT_OPTIONS)),
};

export { TableContainer };
